// Audio timeline maths for the editing suite (pure). SFX can be nudged ±1.5 s from where the cue detector put them
// and levelled from -12 to +6 dB (spec-v2.1.md appendix: PATCH .../shots/:sceneId { sfx: [{ index, atS, gainDb }] }).
import type { AdScene, ShotSfx } from "./types";

export const NUDGE_S = 1.5;
export const GAIN_MIN = -12;
export const GAIN_MAX = 6;

const round = (v: number, step: number) => Math.round(v / step) * step;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Where a dragged effect may land: within ±1.5 s of its detected time and inside its scene, to 0.05 s. */
export function clampNudge(detectedAtS: number, proposedS: number, sceneDurS: number): number {
  const lo = Math.max(0, detectedAtS - NUDGE_S);
  const hi = Math.min(sceneDurS, detectedAtS + NUDGE_S);
  return +clamp(round(proposedS, 0.05), lo, Math.max(lo, hi)).toFixed(2);
}

export const clampGain = (db: number) => +clamp(round(db, 0.5), GAIN_MIN, GAIN_MAX).toFixed(1);

/** Each scene's start time in the ad. */
export function sceneStarts(scenes: Pick<AdScene, "durationS">[]): number[] {
  let t = 0;
  return scenes.map((s) => {
    const at = t;
    t += s.durationS;
    return at;
  });
}
export const totalLength = (scenes: Pick<AdScene, "durationS">[]) => scenes.reduce((a, s) => a + s.durationS, 0);

/** Pixels dragged on a lane `widthPx` wide that shows `totalS` seconds, in seconds. */
export const pxToSeconds = (dxPx: number, widthPx: number, totalS: number) => (widthPx > 0 ? (dxPx / widthPx) * totalS : 0);

/** The edit to send for one effect (only what changed). */
export function sfxEdit(index: number, before: ShotSfx, after: Partial<Pick<ShotSfx, "atS" | "gainDb" | "enabled">>) {
  const e: { index: number; atS?: number; gainDb?: number; enabled?: boolean } = { index };
  if (after.atS !== undefined && after.atS !== before.atS) e.atS = after.atS;
  if (after.gainDb !== undefined && after.gainDb !== before.gainDb) e.gainDb = after.gainDb;
  if (after.enabled !== undefined && after.enabled !== before.enabled) e.enabled = after.enabled;
  return e;
}

/** "0:05.2" for marker labels. */
export function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r.toFixed(1).padStart(4, "0")}`;
}
